/**
 * Lettera d'incarico di un'offerta consulente: si genera, si fa firmare, si
 * ricarica firmata. Il caricamento della copia firmata rende attiva l'offerta
 * se era ancora da approvare: la firma è l'accettazione.
 *
 * Prima di generarla si fissano le condizioni che cambiano da incarico a
 * incarico — rimborsi di km, vitto e alloggio, esclusiva e penale — che
 * restano salvate sull'offerta: rigenerando la lettera non si perdono.
 *
 * Compatto, per stare nella riga di una tabella (commessa, offerte consulenti).
 */
import { useRef, useState } from 'react';
import { offerteConsulentiApi, allegatiApi } from '../api';
import Modal from './Modal';
import CampoImporto from './CampoImporto';

const label = 'block text-xs font-medium text-gray-500 mb-0.5';
const vuotoSeNull = (v) => (v === null || v === undefined ? '' : v);

export default function IncaricoConsulente({ offerta, onCambiato }) {
  const [lavorando, setLavorando] = useState(false);
  const [finestra, setFinestra] = useState(null);   // condizioni in compilazione
  const input = useRef(null);
  const nome = `Incarico_${(offerta.consulente_cognome || offerta.consulente_nome || 'consulente')}`
    .replace(/[^\w-]+/g, '_');

  const apri = () => setFinestra({
    // Km: quanto concordato per l'incarico, altrimenti la tariffa del profilo
    rimborso_km: vuotoSeNull(offerta.rimborso_km ?? offerta.consulente_km),
    rimborso_vitto: vuotoSeNull(offerta.rimborso_vitto),
    rimborso_alloggio: vuotoSeNull(offerta.rimborso_alloggio),
    esclusiva: offerta.esclusiva !== false,
    penale_esclusiva: vuotoSeNull(offerta.penale_esclusiva),
    firma_con_offerta: !!offerta.incarico_firma_offerta,
  });
  // Si può citare l'offerta del consulente se ne abbiamo almeno il numero;
  // "allegata" nella lettera si scrive solo se il file c'è davvero.
  const haOfferta = !!(offerta.numero || offerta.preventivo_id);
  const campo = (k, v) => setFinestra(f => ({ ...f, [k]: v }));

  const genera = async (formato) => {
    setLavorando(true);
    try {
      await offerteConsulentiApi.incarico(offerta.id, formato, nome, finestra);
      onCambiato?.();   // le condizioni ora sono salvate sull'offerta
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  const carica = async (file) => {
    if (!file) return;
    setLavorando(true);
    try {
      const r = await offerteConsulentiApi.caricaAllegato(offerta.id, file, 'incarico_firmato');
      if (r.offerta_attivata) alert('Incarico firmato caricato: l\'offerta del consulente è ora attiva.');
      onCambiato?.();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  return (
    <div className="flex items-center gap-1 justify-end">
      {offerta.incarico_firmato_id ? (
        <button className="text-xs text-green-700 hover:underline whitespace-nowrap" disabled={lavorando}
                title="Apri la lettera d'incarico firmata"
                onClick={() => allegatiApi.scarica(offerta.incarico_firmato_id, offerta.incarico_firmato_nome)
                  .catch(e => alert(e))}>
          ✓ firmato
        </button>
      ) : (
        <>
          <button className="btn-secondary btn-sm px-2 whitespace-nowrap" disabled={lavorando}
                  title="Prepara la lettera d'incarico da far firmare" onClick={apri}>
            📄 Incarico
          </button>
          <button className="btn-secondary btn-sm px-2 whitespace-nowrap" disabled={lavorando}
                  title="Carica la lettera firmata dal consulente" onClick={() => input.current?.click()}>
            ⬆ Firmato
          </button>
          <input ref={input} type="file" accept="application/pdf,image/*" className="hidden"
                 onChange={e => { carica(e.target.files?.[0]); e.target.value = ''; }} />
        </>
      )}

      {finestra && (
        <Modal title={`Lettera d'incarico — ${offerta.consulente_nome || ''} ${offerta.consulente_cognome || ''}`}
               onClose={() => setFinestra(null)} size="md">
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Rimborsi spese</div>
              <p className="text-xs text-gray-500 mb-2">
                Lascia vuoto ciò che non è previsto: la lettera dirà che non è rimborsato salvo autorizzazione.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={label}>Km (€/km)</label>
                  <CampoImporto className="input py-1 text-sm" value={finestra.rimborso_km}
                                onChange={v => campo('rimborso_km', v)} />
                </div>
                <div>
                  <label className={label}>Vitto (€ al giorno, max)</label>
                  <CampoImporto className="input py-1 text-sm" value={finestra.rimborso_vitto}
                                onChange={v => campo('rimborso_vitto', v)} />
                </div>
                <div>
                  <label className={label}>Alloggio (€ a notte, max)</label>
                  <CampoImporto className="input py-1 text-sm" value={finestra.rimborso_alloggio}
                                onChange={v => campo('rimborso_alloggio', v)} />
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <label className="flex items-start gap-2">
                <input type="checkbox" className="w-4 h-4 mt-0.5" checked={finestra.esclusiva}
                       onChange={e => campo('esclusiva', e.target.checked)} />
                <span>
                  Clausola di esclusiva
                  <span className="block text-xs text-gray-500">
                    Divieto di lavorare per i clienti Sinthema dell'incarico fino a 12 mesi dalla fine delle
                    attività, attivazione già dalle trattative, obbligo di segnalare i contatti diretti,
                    risoluzione di diritto in caso di violazione.
                  </span>
                </span>
              </label>
              {finestra.esclusiva && (
                <div className="mt-2 ml-6 w-48">
                  <label className={label}>Penale per violazione (€, facoltativa)</label>
                  <CampoImporto className="input py-1 text-sm" value={finestra.penale_esclusiva}
                                onChange={v => campo('penale_esclusiva', v)} />
                </div>
              )}
            </div>

            <div className="border-t pt-3">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Accettazione</div>
              {haOfferta ? (
                <>
                  <p className="text-xs text-gray-500 mb-2">
                    La lettera cita l'offerta del consulente
                    {offerta.numero ? ` n. ${offerta.numero}` : ''}
                    {offerta.preventivo_nome ? ` (${offerta.preventivo_nome}, allegata)` : ''}
                    {' '}e la richiama come parte integrante dell'incarico.
                  </p>
                  <label className="flex items-start gap-2">
                    <input type="checkbox" className="w-4 h-4 mt-0.5" checked={finestra.firma_con_offerta}
                           onChange={e => campo('firma_con_offerta', e.target.checked)} />
                    <span>
                      L'offerta vale come accettazione: niente copia firmata
                      <span className="block text-xs text-gray-500">
                        La lettera dice che l'incarico fa seguito alla sua offerta e che non serve restituirla
                        firmata; le condizioni in più si intendono accettate con l'avvio delle attività.
                      </span>
                    </span>
                  </label>
                  {finestra.firma_con_offerta && finestra.esclusiva && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                      L'esclusiva{Number(finestra.penale_esclusiva) > 0 ? ' e la penale' : ''} non
                      {' '}{Number(finestra.penale_esclusiva) > 0 ? 'sono' : 'è'} nell'offerta del consulente:
                      senza firma {Number(finestra.penale_esclusiva) > 0 ? 'valgono' : 'vale'} solo per fatti
                      concludenti. Se ci tieni, fatti restituire la lettera firmata.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-500">
                  Nessuna offerta del consulente registrata: la lettera chiede la copia firmata per accettazione.
                  Caricando la sua offerta fra gli allegati, la lettera può citarla e sostituire la firma.
                </p>
              )}
            </div>

            <p className="text-xs text-gray-400">
              Le condizioni restano salvate sull'offerta del consulente. Il testo è una base: in Word puoi adattarlo.
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setFinestra(null)}>Chiudi</button>
            <button className="btn-secondary" disabled={lavorando} onClick={() => genera('docx')}>
              {lavorando ? '…' : '⬇ Word'}
            </button>
            <button className="btn-primary" disabled={lavorando} onClick={() => genera('pdf')}>
              {lavorando ? '…' : '⬇ PDF'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
